import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { myWhatsAppStatusText } from '@/lib/comm/systemWhatsApp';
import { INVALID_WHATSAPP_MESSAGE, isValidWhatsAppPhone } from '@/lib/comm/phone';
import { cn } from '@/lib/utils';

const sb = supabase as any;
export const WHATSAPP_PHONE_ANCHOR = 'avisos-whatsapp';

interface MyWhatsApp {
  eligible: boolean;
  whatsapp_phone: string | null;
  whatsapp_status: string;
  important_enabled: boolean;
  digest_enabled: boolean;
  opted_out_at: string | null;
}

export function useMyWhatsApp(enabled = true) {
  return useQuery({
    queryKey: ['comm-my-whatsapp'],
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await sb.rpc('comm_my_whatsapp');
      if (error) throw error;
      return data as MyWhatsApp;
    },
  });
}

interface Props {
  /** Controlled mode: the page form owns the phone and saves it. */
  phone?: string;
  onPhoneChange?: (v: string) => void;
  /** Standalone mode: saves profiles.phone for this user. */
  userId?: string;
  initialPhone?: string | null;
  className?: string;
  mutedClass?: string;
}

/** "Avisos pelo WhatsApp" for the signed-in user; hidden for users outside the system WhatsApp. */
export function WhatsAppAlertsSection({ phone, onPhoneChange, userId, initialPhone, className, mutedClass = 'text-muted-foreground' }: Props) {
  const qc = useQueryClient();
  const { data } = useMyWhatsApp();
  const [localPhone, setLocalPhone] = useState(initialPhone ?? '');
  const [savingPhone, setSavingPhone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedPhone, setSavedPhone] = useState(initialPhone ?? '');
  const standalone = onPhoneChange === undefined && !!userId;
  const { data: profilePhone } = useQuery({
    queryKey: ['comm-my-profile-phone', userId],
    enabled: standalone && initialPhone == null,
    queryFn: async () => {
      const { data: row } = await sb.from('profiles').select('phone').eq('id', userId).maybeSingle();
      return (row?.phone as string | null) ?? '';
    },
  });
  useEffect(() => {
    if (profilePhone != null) { setLocalPhone(profilePhone); setSavedPhone(profilePhone); }
  }, [profilePhone]);
  if (!data?.eligible) return null;

  const controlled = onPhoneChange !== undefined;
  const value = controlled ? phone ?? '' : localPhone;
  const invalid = value.trim() !== '' && !isValidWhatsAppPhone(value);
  const enabled = data.whatsapp_status !== 'opted_out';

  const update = async (next: { enabled?: boolean; important?: boolean; digest?: boolean }) => {
    setSaving(true);
    try {
      const { error } = await sb.rpc('comm_update_my_whatsapp', {
        _enabled: next.enabled ?? enabled,
        _important: next.important ?? data.important_enabled,
        _digest: next.digest ?? data.digest_enabled,
      });
      if (error) throw error;
      await qc.invalidateQueries({ queryKey: ['comm-my-whatsapp'] });
    } catch (e) {
      toast.error('Não foi possível salvar: ' + (e as Error).message);
    } finally { setSaving(false); }
  };

  const savePhone = async () => {
    if (!userId || invalid || !value.trim()) return;
    setSavingPhone(true);
    try {
      const { data: rows, error } = await sb.from('profiles').update({ phone: value.replace(/\D/g, '') }).eq('id', userId).select('id');
      if (error) throw error;
      if (!rows?.length) throw new Error('Sem permissão para salvar');
      setSavedPhone(value);
      toast.success('WhatsApp salvo');
      await qc.invalidateQueries({ queryKey: ['comm-my-whatsapp'] });
    } catch (e) {
      toast.error('Não foi possível salvar o WhatsApp: ' + (e as Error).message);
    } finally { setSavingPhone(false); }
  };

  return (
    <Card id={WHATSAPP_PHONE_ANCHOR} className={cn('scroll-mt-24', className)}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5 text-primary" />
          Avisos pelo WhatsApp
        </CardTitle>
        <CardDescription className={mutedClass}>{myWhatsAppStatusText(data.whatsapp_status, data.opted_out_at)}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="whatsapp-phone-input">WhatsApp com DDD</Label>
          <div className="flex gap-2">
            <Input
              id="whatsapp-phone-input"
              type="tel"
              maxLength={20}
              placeholder="(85) 98765-4321"
              value={value}
              onChange={(e) => (controlled ? onPhoneChange!(e.target.value) : setLocalPhone(e.target.value))}
            />
            {!controlled && (
              <Button type="button" variant="outline" onClick={savePhone}
                disabled={savingPhone || invalid || !value.trim() || value === savedPhone}>
                {savingPhone && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salvar
              </Button>
            )}
          </div>
          {invalid && <p className="text-xs text-destructive">{INVALID_WHATSAPP_MESSAGE}</p>}
          {controlled && <p className={cn('text-xs', mutedClass)}>O telefone é salvo com o botão "Salvar alterações" do perfil.</p>}
        </div>
        <div className="flex items-center justify-between gap-3">
          <Label htmlFor="whatsapp-enabled">Receber avisos pelo WhatsApp</Label>
          <Switch id="whatsapp-enabled" checked={enabled} disabled={saving} onCheckedChange={(v) => update({ enabled: v })} />
        </div>
        <div className={cn('space-y-3', !enabled && 'opacity-50')}>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={data.important_enabled} disabled={saving || !enabled} onCheckedChange={(v) => update({ important: v === true })} />
            <span>Avisos importantes <span className={mutedClass}>(queda do WhatsApp do CRM, chamados, faturas)</span></span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={data.digest_enabled} disabled={saving || !enabled} onCheckedChange={(v) => update({ digest: v === true })} />
            <span>Resumos e comunicados</span>
          </label>
        </div>
      </CardContent>
    </Card>
  );
}
